import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, Mail, Eye, Save, Send, History, Power } from "lucide-react";

const CAT = { automated: "bg-slate-100 text-slate-600", human: "bg-[#003768]/10 text-[#003768]", early_access: "bg-[#78be21]/15 text-[#4f8a10]" };
const CAT_LABEL = { automated: "Automated", human: "Human-initiated", early_access: "Early Access / Preview" };
const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

export function AdminEmailTemplates() {
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("templates");
  const [log, setLog] = useState(null);
  const [open, setOpen] = useState(null); // template key
  const [tpl, setTpl] = useState(null);
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/email/templates").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "history") api.get("/admin/email/log").then(({ data }) => setLog(data)).catch(() => setLog([])); }, [tab]);

  const openTpl = async (key) => {
    setOpen(key); setTpl(null); setPreview(null);
    const { data } = await api.get(`/admin/email/templates/${key}`);
    setTpl(data);
    setForm({ subject: data.subject, heading: data.heading, body: data.body, cta_label: data.cta_label });
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doPreview = async () => {
    setBusy("preview");
    try { const { data } = await api.post(`/admin/email/templates/${open}/preview`, form); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await api.put(`/admin/email/templates/${open}`, form); toast.success("Master template saved"); await load(); await openTpl(open); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try { const { data } = await api.post(`/admin/email/templates/${open}/test`, form); toast.success(`Test email sent to ${data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test send failed"); }
    finally { setBusy(""); }
  };
  const toggleStatus = async () => {
    const next = tpl.status === "inactive" ? "active" : "inactive";
    setBusy("status");
    try { await api.put(`/admin/email/templates/${open}`, { status: next }); toast.success(next === "active" ? "Template activated" : "Template deactivated"); await load(); await openTpl(open); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update status"); }
    finally { setBusy(""); }
  };

  return (
    <DashboardShell title="Communications" subtitle="Email templates & history — GPA+ LSC-Aligned Professional Communication Standard" nav={ADMIN_NAV}>
      <div className="mb-5 flex gap-2">
        <Button variant={tab === "templates" ? "default" : "outline"} onClick={() => setTab("templates")} data-testid="tab-templates" className={tab === "templates" ? "bg-[#003768]" : ""}><Mail className="mr-1.5 h-4 w-4" /> Email Templates</Button>
        <Button variant={tab === "history" ? "default" : "outline"} onClick={() => setTab("history")} data-testid="tab-history" className={tab === "history" ? "bg-[#003768]" : ""}><History className="mr-1.5 h-4 w-4" /> Email History</Button>
      </div>

      {tab === "templates" && (
        <Card className="p-2 card-elevate sm:p-4">
          {!list && <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003768]" /></div>}
          {list && (
            <Table className="text-[13px]">
              <TableHeader><TableRow className="[&_th]:text-[11px] [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-slate-400">
                <TableHead>Template</TableHead><TableHead>Purpose / Trigger</TableHead><TableHead>Subject</TableHead>
                <TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Last Updated</TableHead><TableHead>By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {list.map((t) => (
                  <TableRow key={t.key} className="cursor-pointer [&_td]:py-2 hover:bg-slate-50" onClick={() => openTpl(t.key)} data-testid={`tpl-row-${t.key}`}>
                    <TableCell className="font-medium text-[#003768]">{t.name}</TableCell>
                    <TableCell className="max-w-[260px] text-slate-500">{t.purpose}</TableCell>
                    <TableCell className="text-slate-600">{t.subject}</TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CAT[t.category]}`}>{CAT_LABEL[t.category] || t.category}</span></TableCell>
                    <TableCell><span className="font-medium text-emerald-600">{t.status === "inactive" ? "Inactive" : "Active"}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{fmt(t.updated_at)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{t.updated_by || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <FinePrint>
            Communication Standard: GPA+ LSC-Aligned. This describes our professional design objective and does not imply
            formal Lone Star College approval. Action links and expiration notices are added automatically and cannot be
            removed from a template. An official LSC-branded email header requires LSC Marketing &amp; Communications approval —
            flagged for future institutional review.
          </FinePrint>
        </Card>
      )}

      {tab === "history" && (
        <Card className="p-2 card-elevate sm:p-4">
          {!log && <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003768]" /></div>}
          {log && (
            <Table className="text-[13px]">
              <TableHeader><TableRow className="[&_th]:text-[11px] [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-slate-400">
                <TableHead>Date / Time</TableHead><TableHead>Recipient</TableHead><TableHead>Template</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Initiated By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {log.map((r, i) => (
                  <TableRow key={i} className="[&_td]:py-2" data-testid={`log-row-${i}`}>
                    <TableCell className="text-xs text-slate-500">{fmt(r.at)}</TableCell>
                    <TableCell className="text-slate-600">{r.to}</TableCell>
                    <TableCell className="text-slate-600">{r.template_key || "—"}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-slate-600">{r.subject}</TableCell>
                    <TableCell><span className={`font-medium ${r.status === "failed" ? "text-rose-600" : "text-emerald-600"}`}>{r.status}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{r.initiated_by || "system"}</TableCell>
                  </TableRow>
                ))}
                {log.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">No emails sent yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          <FinePrint>Operational metadata only — recipient, template, subject, status and time. Links, tokens and credentials are never stored.</FinePrint>
        </Card>
      )}

      {/* Editor */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="tpl-editor">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003768]">{tpl?.name || "Template"}</DialogTitle></DialogHeader>
          {!tpl && <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003768]" /></div>}
          {tpl && (
            <div className="space-y-4">
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                <b>Communication Standard: GPA+ LSC-Aligned.</b> {tpl.purpose} Available merge fields:{" "}
                {tpl.tokens.map((t) => <code key={t} className="mx-0.5 rounded bg-white px-1 text-[11px]">{`{{${t}}}`}</code>)}
                {tpl.uses_link && <> — the secure action link &amp; expiration notice are added automatically.</>}
              </p>
              <div><Label>Subject</Label><Input value={form.subject || ""} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" data-testid="tpl-subject" /></div>
              <div><Label>Heading</Label><Input value={form.heading || ""} onChange={(e) => set("heading", e.target.value)} className="mt-1.5" data-testid="tpl-heading" /></div>
              <div><Label>Body</Label><Textarea value={form.body || ""} onChange={(e) => set("body", e.target.value)} className="mt-1.5 min-h-[160px]" data-testid="tpl-body" /><p className="mt-1 text-[11px] text-slate-400">Separate paragraphs with a blank line.</p></div>
              {tpl.uses_link && <div><Label>Button label</Label><Input value={form.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className="mt-1.5" data-testid="tpl-cta" /></div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={doPreview} disabled={busy === "preview"} data-testid="tpl-preview-btn">{busy === "preview" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Preview</Button>
                <Button variant="outline" onClick={sendTest} disabled={busy === "test"} data-testid="tpl-test-btn">{busy === "test" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Send Test Email</Button>
                <Button variant="outline" onClick={toggleStatus} disabled={busy === "status"} data-testid="tpl-status-btn" className={tpl.status === "inactive" ? "text-emerald-600" : "text-slate-500"}>{busy === "status" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Power className="mr-1.5 h-4 w-4" />} {tpl.status === "inactive" ? "Activate" : "Deactivate"}</Button>
                <Button onClick={save} disabled={busy === "save"} className="ml-auto bg-[#003768] hover:bg-[#00284d]" data-testid="tpl-save-btn">{busy === "save" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save Changes</Button>
              </div>
              {preview && (
                <div className="rounded-xl border border-slate-200" data-testid="tpl-preview">
                  <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">Subject: <b className="text-[#003768]">{preview.subject}</b></p>
                  <iframe title="preview" srcDoc={preview.html} className="h-[420px] w-full rounded-b-xl" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
